import { useTranslation } from "react-i18next";
import * as PriceUtils from "~/utils/PriceUtils";
import { Button, CardContent, Stack, styled, Typography } from "~/common/mui";
import Tag from "~/common/components/Tag/Tag";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { Param } from "../common/components/v2/Params";
import { C_WHITE } from "~/common/components/icon/Colors";
import type { QuoteListRow } from "../quoteList/useQuoteListState";
import { isMultiAccountOrderSearchEnabled } from "~/utils/features";
import { getQuoteStatus, isErrorEntry } from "../common/utils/util";

interface Props {
  row: QuoteListRow;
  sticky?: boolean;
  renderHeaderCheckbox: (row: QuoteListRow) => JSX.Element;
}

export const QuoteCardHeader = ({ row, sticky, renderHeaderCheckbox }: Props) => {
  const { t } = useTranslation();

  return (
    <DetailsSummary sticky={sticky}>
      {renderHeaderCheckbox?.(row)}
      <Param label={t("quoteListPage.table.header.quoteNumber")} value={row.data.soNumber} />
      <Param label={t("quoteListPage.table.header.customerReferences")} value={row.data.poNumber} />
      {isMultiAccountOrderSearchEnabled() && (
        <Stack gap={0.25}>
          <Typography variant="bodyMedium" color="text.primary" component={"b"}>
            {[row.data.account?.shortCode, row.data.account?.name].filter(Boolean).join(", ")}
          </Typography>
          <Typography variant="bodySmall" color="text.primary">
            {[row.data.account?.endCustomerCode, row.data.account?.endCustomerName].filter(Boolean).join(", ")}
          </Typography>
        </Stack>
      )}
      <Param
        label={t("quoteListPage.table.header.date")}
        value={formatNullableDate(row.data.datePlaced, DateFormatType.DATE)}
      />
      <Param
        label={t("quoteListPage.table.header.status")}
        value={<Tag variant={getQuoteStatus(row.data.orderStatus)}>{t(row.data.orderStatus)}</Tag>}
      />

      <Param
        label={t("quoteListPage.table.header.total")}
        value={
          <>
            <Typography component="b" variant="bodyLarge">
              {PriceUtils.text(row.data.totalPrice)} {row.data.hasSurcharge && "*"}
            </Typography>
          </>
        }
      />

      <Button variant="text" href={row.data.detailsUrl} target="_blank" disabled={isErrorEntry(row)}>
        {t("orderListPage.view.details.text")}
      </Button>
    </DetailsSummary>
  );
};

const DetailsSummary = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== "sticky",
})<{ sticky?: boolean }>(({ theme, sticky }) => ({
  alignItems: "center",
  padding: "16px",
  display: "grid",
  columnGap: "16px",
  position: sticky ? "sticky" : "static",
  top: sticky ? 0 : "auto",
  zIndex: sticky ? 5 : "auto",
  background: sticky ? C_WHITE : "transparent",
  borderTopLeftRadius: "12px",
  borderTopRightRadius: "12px",
  borderBottom: `1px solid ${theme.palette.divider}`,
  ...(isMultiAccountOrderSearchEnabled()
    ? {
        gridTemplateAreas: "checkbox so po account date total status cta",
        gridTemplateColumns: "auto repeat(2, minmax(0, 1fr)) 332px repeat(3, minmax(0, 1fr)) auto",
      }
    : {
        gridTemplateAreas: "checkbox so po date total status cta",
        gridTemplateColumns: "auto repeat(5, minmax(0, 1fr)) auto",
      }),
}));
